import React from "react";
import { PHASE_3_PALETTE } from "./palette";

// Phase 3 (B1): vertical two-bar comparison chart used at the bottom of every
// per-object detail page (PDF pages 48–107). For each cost category (Chauffage,
// Climatisation, Electricité, Eau, Entretien) the object's percentage is shown
// next to the building's category average. The mint bar is the object's share,
// the cobalt bar is the building's share.
//
// Props
//   data         Array<{ label: string, object: number, immeuble: number }>
//                values are already percentages (e.g. 36.8 not 0.368)
//   width        number  default 720 fits inside A4 portrait content area
//   height       number  default 200
//   objectLabel  legend  default "Objet"
//   immeubleLabel legend default "Immeuble"
const ObjectVsImmeubleChart = ({
  data,
  width = 720,
  height = 200,
  objectLabel = "Objet",
  immeubleLabel = "Immeuble",
}) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const padding = { top: 30, right: 16, bottom: 26, left: 32 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const maxValue = Math.max(
    10,
    ...data.flatMap((d) => [Math.abs(d.object || 0), Math.abs(d.immeuble || 0)])
  );
  // Round max up to next 10 so axis lines look clean (10, 20, 30, ...).
  const niceMax = Math.ceil(maxValue / 10) * 10;

  const groupCount = data.length;
  const groupWidth = innerWidth / groupCount;
  const barWidth = Math.min(28, (groupWidth - 12) / 2);
  const yScale = (v) => (Math.abs(v) / niceMax) * innerHeight;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ fontFamily: "system-ui, sans-serif" }}>
      {/* Legend */}
      <g transform={`translate(${padding.left}, 14)`} fontSize="10" fill={PHASE_3_PALETTE.text}>
        <rect x="0" y="-8" width="12" height="10" fill={PHASE_3_PALETTE.current} />
        <text x="16" y="0">{objectLabel}</text>
        <rect x="70" y="-8" width="12" height="10" fill={PHASE_3_PALETTE.prior} />
        <text x="86" y="0">{immeubleLabel}</text>
      </g>

      {/* Y-axis baseline */}
      <line
        x1={padding.left}
        y1={padding.top + innerHeight}
        x2={padding.left + innerWidth}
        y2={padding.top + innerHeight}
        stroke={PHASE_3_PALETTE.muted}
        strokeWidth="0.5"
      />

      {/* Bars */}
      {data.map((row, i) => {
        const groupX = padding.left + i * groupWidth + groupWidth / 2;
        const objH = yScale(row.object);
        const immH = yScale(row.immeuble);
        const baselineY = padding.top + innerHeight;
        return (
          <g key={row.label + "_" + i}>
            {/* Object bar (left) */}
            <rect
              x={groupX - barWidth - 2}
              y={baselineY - objH}
              width={barWidth}
              height={objH}
              fill={PHASE_3_PALETTE.current}
            />
            <text
              x={groupX - barWidth - 2 + barWidth / 2}
              y={baselineY - objH - 4}
              textAnchor="middle"
              fontSize="9"
              fill={PHASE_3_PALETTE.text}
            >
              {Number(row.object).toFixed(1)}%
            </text>

            {/* Immeuble bar (right) */}
            <rect
              x={groupX + 2}
              y={baselineY - immH}
              width={barWidth}
              height={immH}
              fill={PHASE_3_PALETTE.prior}
            />
            <text
              x={groupX + 2 + barWidth / 2}
              y={baselineY - immH - 4}
              textAnchor="middle"
              fontSize="9"
              fill={PHASE_3_PALETTE.text}
            >
              {Number(row.immeuble).toFixed(1)}%
            </text>

            {/* Category label */}
            <text
              x={groupX}
              y={baselineY + 14}
              textAnchor="middle"
              fontSize="10"
              fill={PHASE_3_PALETTE.text}
            >
              {row.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default ObjectVsImmeubleChart;
